import { HttpErrorResponse } from '@angular/common/http';

import type { UiError, UiErrorKind, UiValidationIssue } from '../models/ui-error.models';

export interface NormalizeErrorOptions {
  fallbackMessage?: string;
}

const DEFAULT_FALLBACK = 'Request failed. Please try again.';
const NETWORK_MESSAGE = 'Unable to reach the server. Check your connection and try again.';
const SERVER_MESSAGE = 'The server had a problem. Please try again.';

const asString = (value: unknown): string | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();
  return trimmed.length > 0 ? trimmed : undefined;
};

const asRecord = (value: unknown): Record<string, unknown> | undefined => {
  if (!value || typeof value !== 'object') {
    return undefined;
  }

  return value as Record<string, unknown>;
};

const asArray = (value: unknown): unknown[] | undefined => {
  if (!Array.isArray(value)) {
    return undefined;
  }

  return value;
};

const parseJsonStringRecord = (value: unknown): Record<string, unknown> | undefined => {
  if (typeof value !== 'string') {
    return undefined;
  }

  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return undefined;
  }

  try {
    const parsed = JSON.parse(trimmed);
    return asRecord(parsed);
  } catch {
    return undefined;
  }
};

const asErrorPayloadRecord = (payload: unknown): Record<string, unknown> | undefined => {
  return asRecord(payload) ?? parseJsonStringRecord(payload);
};

const extractBackendMessage = (payload: unknown): string | undefined => {
  const payloadRecord = asErrorPayloadRecord(payload);
  const direct = asString(payloadRecord?.['message']);
  if (direct) {
    return direct;
  }

  const firstNested = asRecord(payloadRecord?.['error']) ?? parseJsonStringRecord(payloadRecord?.['error']);
  const nestedMessage = asString(firstNested?.['message']);
  if (nestedMessage) {
    return nestedMessage;
  }

  const secondNested = asRecord(firstNested?.['error']);
  return asString(secondNested?.['message']);
};

const extractRequestId = (payload: unknown): string | undefined => {
  const payloadRecord = asErrorPayloadRecord(payload);
  const direct = asString(payloadRecord?.['requestId']);
  if (direct) {
    return direct;
  }

  const firstNested = asRecord(payloadRecord?.['error']) ?? parseJsonStringRecord(payloadRecord?.['error']);
  return asString(firstNested?.['requestId']);
};

const normalizeIssuePath = (value: unknown): string[] | undefined => {
  const rawPath = asArray(value);
  if (!rawPath) {
    return undefined;
  }

  const path = rawPath
    .map((segment) => {
      if (typeof segment === 'string') {
        return segment.trim();
      }

      if (typeof segment === 'number') {
        return String(segment);
      }

      return '';
    })
    .filter((segment) => segment.length > 0);

  return path.length > 0 ? path : undefined;
};

const toValidationIssue = (value: unknown): UiValidationIssue | undefined => {
  const issue = asRecord(value);
  if (!issue) {
    return undefined;
  }

  const message = asString(issue['message']);
  if (!message) {
    return undefined;
  }

  const path = normalizeIssuePath(issue['path']);
  const code = asString(issue['code']);

  return {
    message,
    path,
    code,
  };
};

const extractValidationIssues = (payload: unknown): UiValidationIssue[] => {
  const payloadRecord = asErrorPayloadRecord(payload);
  if (!payloadRecord) {
    return [];
  }

  const firstNested = asErrorPayloadRecord(payloadRecord['error']);
  const secondNested = asErrorPayloadRecord(firstNested?.['error']);
  const rawIssues =
    asArray(payloadRecord['issues']) ?? asArray(firstNested?.['issues']) ?? asArray(secondNested?.['issues']) ?? [];

  return rawIssues
    .map((issue) => toValidationIssue(issue))
    .filter((issue): issue is UiValidationIssue => Boolean(issue));
};

const pickClientMessage = (
  backendMessage: string | undefined,
  issues: UiValidationIssue[],
  fallbackMessage: string,
): string => {
  if (issues.length > 0) {
    if (!backendMessage) {
      return issues[0].message;
    }

    const normalizedMessage = backendMessage.toLowerCase();
    if (normalizedMessage === 'validation failed' || normalizedMessage === 'invalid request') {
      return issues[0].message;
    }
  }

  return backendMessage ?? fallbackMessage;
};

const classifyStatus = (status: number): UiErrorKind => {
  if (status === 0) {
    return 'network';
  }

  if (status === 401) {
    return 'auth';
  }

  if (status >= 400 && status < 500) {
    return 'client';
  }

  if (status >= 500) {
    return 'server';
  }

  return 'unknown';
};

export const normalizeApiError = (error: unknown, options: NormalizeErrorOptions = {}): UiError => {
  const fallbackMessage = options.fallbackMessage ?? DEFAULT_FALLBACK;

  if (error instanceof HttpErrorResponse) {
    const statusCode = error.status;
    const kind = classifyStatus(statusCode);
    const backendMessage = extractBackendMessage(error.error);
    const requestId = extractRequestId(error.error);
    const issues = extractValidationIssues(error.error);

    if (kind === 'network') {
      return {
        message: NETWORK_MESSAGE,
        kind,
        statusCode,
      };
    }

    if (kind === 'server') {
      return {
        message: SERVER_MESSAGE,
        kind,
        statusCode,
        requestId,
        issues,
      };
    }

    if (kind === 'client' || kind === 'auth') {
      return {
        message: pickClientMessage(backendMessage, issues, fallbackMessage),
        kind,
        statusCode,
        requestId,
        issues,
      };
    }

    return {
      message: pickClientMessage(backendMessage, issues, fallbackMessage),
      kind,
      statusCode,
      requestId,
      issues,
    };
  }

  if (typeof error === 'object' && error !== null) {
    const maybeMessage = asString((error as { message?: unknown }).message);

    return {
      message: maybeMessage ?? fallbackMessage,
      kind: 'unknown',
    };
  }

  return {
    message: fallbackMessage,
    kind: 'unknown',
  };
};

export const toFriendlyErrorMessage = (error: unknown, fallbackMessage: string): string => {
  return normalizeApiError(error, { fallbackMessage }).message;
};
